import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Compass } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import { MODALITIES } from "@/constants/modalities";
import { EMAIL } from "@/constants/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const QUICK_LINKS = [
  { to: "/insights", label: "Insights journal" },
  { to: "/shielding-requirements", label: "State requirements" },
  { to: "/about", label: "About the practice" },
  { to: "/contact", label: "Contact" },
];

const NotFound = () => {
  usePageMeta(
    "Page Not Found | Heartland Physics",
    "The page you were looking for has moved. Find shielding design services, state requirements, and the insights journal here."
  );
  return (
    <>
      <section data-testid="notfound-page" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-7xl px-5 pb-16 pt-14 md:px-8 md:pb-24 md:pt-20">
          <Reveal y={12}>
            <p className="eyebrow">Page not found</p>
          </Reveal>
          <div className="mt-6 flex flex-wrap items-end gap-x-10 gap-y-6">
            <p
              className="select-none font-heading text-[7rem] font-extrabold leading-none tracking-tight text-transparent md:text-[11rem]"
              style={{ WebkitTextStroke: "2px #0033A0" }}
              data-testid="notfound-numeral"
            >
              404
            </p>
            <h1 className="max-w-md font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-4xl">
              <MaskedLines delay={0.1} lines={["This page moved", "or never existed."]} />
            </h1>
          </div>
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/70 md:text-lg" data-testid="notfound-copy">
              The old Heartland Physics site was rebuilt — some addresses changed. Everything you might be
              looking for is below, or email <a href={`mailto:${EMAIL}`} className="font-semibold text-blueprint hover:text-ink">{EMAIL}</a> and
              we'll point you to it.
            </p>
          </Reveal>
          <Reveal delay={0.55} className="mt-9 flex flex-wrap items-center gap-4">
            <Link
              to="/request"
              data-testid="notfound-cta-request"
              className="group flex items-center gap-2 bg-blueprint px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
            >
              Request a Shielding Design
              <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/"
              data-testid="notfound-cta-home"
              className="group flex items-center gap-2 border border-ink/25 bg-white/60 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint"
            >
              Back to homepage
              <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
          <Reveal>
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.24em] text-ink/50">
              <Compass size={14} className="text-blueprint" /> Where were you headed?
            </p>
          </Reveal>
          <div className="mt-8 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4" data-testid="notfound-links">
            {QUICK_LINKS.map((l, i) => (
              <Reveal key={l.to} delay={i * 0.06} className="h-full">
                <Link
                  to={l.to}
                  data-testid={`notfound-link-${l.to.replace(/\//g, "") || "home"}`}
                  className="group flex h-full items-center justify-between gap-3 bg-white p-6 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
                >
                  {l.label}
                  <ArrowRight size={15} className="shrink-0 text-blueprint transition-transform duration-200 group-hover:translate-x-1 group-hover:text-azure" />
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-12 font-mono text-xs uppercase tracking-[0.24em] text-ink/50">Shielding design by modality</p>
          </Reveal>
          <div className="mt-6 flex flex-wrap gap-2.5" data-testid="notfound-modalities">
            {MODALITIES.map((m) => (
              <Link
                key={m.slug}
                to={`/shielding-design/${m.slug}`}
                data-testid={`notfound-modality-${m.slug}`}
                className="border border-border bg-white px-4 py-2.5 text-xs font-semibold text-ink/70 transition-colors duration-200 hover:border-blueprint hover:text-blueprint"
              >
                {m.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default NotFound;
